import { Dimensions, Metrics, type BreakdownRow, type OverviewResponse } from '@/api/types';
import { metricValue } from '@/api/metric-resolution';
import { BarList } from '@/components/BarList';
import { ExportButton } from '@/components/ExportButton';
import { RangeBar } from '@/components/RangeBar';
import { StatTile } from '@/components/StatTile';
import { formatCompact } from '@/components/TrendChart';
import { breakdown, rowValue, toBars, useOverview } from '@/hooks/useOverview';
import { AnalyticsLoadError, noSourceSentence } from '@/components/AnalyticsLoadError';

/** The site is Arabic-first and aimed at Egypt, so EG is the expected home market. */
const HOME_COUNTRY_HINTS = ['egypt', 'eg'];

/**
 * Where visitors are, and — more usefully — where they CONVERT.
 *
 * A country list on its own only ranks traffic. The interesting question for a single-market
 * landing page is whether the traffic outside the home market is worth anything, which needs
 * leads beside sessions on the same row.
 */
export function GeographyPage({ siteKey }: { siteKey: string }) {
  const { data, isLoading, isError, error, days, setDays, from, to, setRange, isCustomRange } = useOverview(siteKey);

  const countries = breakdown(data, Dimensions.country);
  const cities = breakdown(data, Dimensions.city);
  const languages = breakdown(data, Dimensions.language);

  return (
    <>
      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={data}
        setRange={setRange} isCustomRange={isCustomRange}
        actions={
          <ExportButton
            siteKey={siteKey} from={from} to={to}
            dimension={Dimensions.country} label="Export countries"
          />
        }
      />

      {isLoading && <div className="empty-state"><span className="spinner" /></div>}
      {isError && <AnalyticsLoadError error={error} what="geography" notFound={noSourceSentence(siteKey)} />}

      {data && countries.length === 0 && (
        <div className="empty-state t-body">
          No country rows yet — expected before the tags are live.
        </div>
      )}

      {data && countries.length > 0 && (
        <>
          <div className="kpi-row">
            <StatTile label="Countries reached" value={countries.length} />
            <StatTile label="Cities reached" value={cities.length || null} />
            <HomeMarketTile countries={countries} />
            <StatTile
              label="Languages"
              value={languages.length || null}
              sub={languages.map((l) => l.value).slice(0, 3).join(', ') || undefined}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            <Panel title="Countries" rows={toBars(countries)} />
            <Panel title="Cities" rows={toBars(cities)} />
          </div>

          <CountryPerformanceTable rows={countries} data={data} />
        </>
      )}
    </>
  );
}

/**
 * Share of traffic from the home market. On a geo-targeted landing page a sudden fall here is
 * usually a bot farm or a campaign leaking to the wrong audience — not organic growth.
 */
function HomeMarketTile({ countries }: { countries: BreakdownRow[] }) {
  const total = countries.reduce((sum, row) => sum + rowValue(row), 0);
  if (total === 0) return <StatTile label="Home market share" value={null} />;

  const home = countries
    .filter((row) => HOME_COUNTRY_HINTS.includes(row.value.trim().toLowerCase()))
    .reduce((sum, row) => sum + rowValue(row), 0);

  const pct = (home / total) * 100;

  return (
    <div className="card stat-tile">
      <div className="stat-label t-label">Home market (Egypt)</div>
      <div className="stat-value">{pct.toFixed(0)}%</div>
      <div className="stat-sub t-caption">
        {formatCompact(home)} of {formatCompact(total)} sessions
      </div>
    </div>
  );
}

/**
 * Sessions vs leads per country. A table, not a chart: sessions and leads live on different
 * scales, and a dual-axis chart is the one form the visualisation rules rule out entirely.
 */
function CountryPerformanceTable({ rows, data }: { rows: BreakdownRow[]; data: OverviewResponse }) {
  const leadsByCountry = new Map<string, number>();
  breakdown(data, Dimensions.country).forEach((row) => {
    const leads = row.metrics[Metrics.leads];
    if (leads !== undefined) leadsByCountry.set(row.value, leads);
  });

  const hasLeads = leadsByCountry.size > 0;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Country performance</h2>
        {!hasLeads && <span className="t-label subtle">leads appear once the backend is connected</span>}
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Country</th>
            <th className="num">Sessions</th>
            <th className="num">Users</th>
            {hasLeads && <th className="num">Leads</th>}
            {hasLeads && <th className="num">Rate</th>}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((row) => {
            const sessions = rowValue(row);
            const leads = leadsByCountry.get(row.value) ?? 0;
            return (
              <tr key={row.value}>
                <td>{row.value || '(unknown)'}</td>
                <td className="num">{formatCompact(sessions)}</td>
                <td className="num">
                  {fmt(metricValue(row.metrics, 'users') ?? undefined)}
                </td>
                {hasLeads && <td className="num">{leads.toLocaleString('en-US')}</td>}
                {hasLeads && (
                  <td className="num">
                    {sessions > 0 ? `${((leads / sessions) * 100).toFixed(2)}%` : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: { label: string; value: number }[] }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        <span className="t-label subtle">{rows.length}</span>
      </div>
      <div className="card-body">
        <BarList rows={rows} limit={12} emptyLabel="Not reported yet." />
      </div>
    </div>
  );
}

function fmt(value: number | undefined): string {
  return value === undefined ? '—' : formatCompact(value);
}
